import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';
import ComicCard from '@/components/feature/ComicCard';
import { categories, getComicsByCategory, getCategoryName } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  const filteredComics = getComicsByCategory(slug);
  const categoryName = getCategoryName(slug);
  const canonicalUrl = typeof window !== 'undefined' ? `${window.location.origin}/category/${slug}` : '';

  return (
    <>
      <SEO
        title={`${categoryName}韩漫_${categoryName}韩国漫画_韩漫之家`}
        description={`韩漫之家为你精选${categoryName}分类韩国漫画，提供无删减下拉式免费在线阅读。收录热门${categoryName}韩漫，每日更新。`}
        keywords={`${categoryName}韩漫, ${categoryName}韩国漫画, 韩漫在线, 韩漫之家`}
        canonicalUrl={canonicalUrl}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: t('breadcrumb.home'), href: '/' },
                  { label: t('breadcrumb.category'), href: '/category/all' },
                  { label: categoryName },
                ]}
              />

              <div className="pb-6">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {slug === 'all' ? '全部漫画' : `${categoryName}漫画`}
                </h1>
                <p className="text-sm text-foreground-500 mt-2">
                  共 {filteredComics.length} 部作品
                </p>
              </div>

              <div className="flex flex-wrap gap-2 pb-8">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                      slug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 border border-background-200/70'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </div>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6" data-product-shop>
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 mt-4">该分类下暂无漫画</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}